import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Anchor, Box, Divider, Flex, Menu, Stack } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { SelectDatesRangeOptionMenu } from './select-dates-range-option-menu';
import { findMatchingDatesRangeOption, } from './date-range-options';
import classes from './date-range-filter.module.css';
const getSelectedLabel = (value) => {
    const hasSelectedRange = value.every((v) => v !== null);
    if (hasSelectedRange && value[0] && value[1]) {
        if (dayjs(value[0]).isSame(value[1], 'day')) {
            if (dayjs(value[0]).isSame(dayjs(), 'day')) {
                return 'Today';
            }
            return dayjs(value[0]).format('D MMM YYYY');
        }
        return value.map((v) => dayjs(v).format('D MMM YYYY')).join(' - ');
    }
    return undefined;
};
/**
 * A filter component that allows users to date ranges from a dropdown menu and displays the selection.
 */
export const DateRangeFilterDropdown = ({ clearable = false, label, onChange: onChangeProp, value: valueProp, menuProps, rangeOptions, onClose, initialValue, onRangeOptionChange: onRangeOptionChangeProp, rangeOptionsTitle, bottomSection, ...buttonProps }) => {
    const [valueInternal, setValueInternal] = useState(initialValue ?? [null, null]);
    const [rangeOption, setRangeOption] = useState(() => {
        if (rangeOptions && initialValue) {
            return findMatchingDatesRangeOption(initialValue, rangeOptions);
        }
        return undefined;
    });
    const [date, setDate] = useState(valueProp?.[0] ?? new Date());
    const onChange = (val) => {
        const selectedLabel = rangeOption?.label ?? getSelectedLabel(val);
        setValueInternal(val);
        onChangeProp?.(val, selectedLabel);
    };
    const onChangeRangeOption = (rangeOption) => {
        const selectedLabel = rangeOption?.label ?? getSelectedLabel(rangeOption.range);
        setRangeOption(rangeOption);
        setValueInternal(rangeOption.range);
        setDate(rangeOption.range[0] ?? new Date());
        onRangeOptionChangeProp?.(rangeOption);
        onChangeProp?.(rangeOption.range, selectedLabel);
    };
    const reset = () => {
        const selectedLabel = rangeOption?.label ?? getSelectedLabel(value);
        setValueInternal([null, null]);
        onChangeProp?.([null, null], selectedLabel);
        setRangeOption(undefined);
        onRangeOptionChangeProp?.(undefined);
    };
    const value = valueProp ?? valueInternal;
    useEffect(() => {
        if (rangeOptions && value) {
            const matchingRangeOption = findMatchingDatesRangeOption(value, rangeOptions);
            setRangeOption(matchingRangeOption);
            if (matchingRangeOption?.range[0]) {
                setDate(matchingRangeOption?.range[0]);
            }
            onRangeOptionChangeProp?.(matchingRangeOption);
        }
    }, [value]);
    const hasSelectedRange = value.every((v) => v !== null);
    const height = [320, clearable ? 30 : 0].reduce((acc, current) => acc + current, 0);
    const titleHeight = rangeOptionsTitle ? 44 : 0;
    const scrollAreaHeight = [height, -titleHeight].reduce((acc, current) => acc + current, 0);
    return (<>
      <Box h={height}>
        <Flex wrap='nowrap' align='stretch' gap={0}>
          {rangeOptions && rangeOptions.length > 0 && (<Flex className={classes.selectDateRangeMenu} h='100%' direction='column'>
              {rangeOptionsTitle && (<Box h={titleHeight}>
                  <Menu.Label fw={700} c='dark' px='md' py='xs' fz='sm'>
                    {rangeOptionsTitle}
                  </Menu.Label>
                  <Divider />
                </Box>)}
              <SelectDatesRangeOptionMenu onChange={onChangeRangeOption} rangeOptions={rangeOptions} value={rangeOption} scrollAreaHeight={scrollAreaHeight}/>
            </Flex>)}
          <Box h='100%' w={1} bg='red'/>
          <Stack mx='sm' my='xs' gap='sm'>
            <DatePicker type='range' value={value} allowSingleDateInRange onChange={(value) => onChange([value[0], value[1] ? dayjs(value[1]).endOf('day').toDate() : null])} date={date} onDateChange={setDate}/>
            {clearable && (<Anchor role='button' onClick={reset} size='xs' c={!hasSelectedRange ? 'dimmed' : buttonProps.color} td={!hasSelectedRange ? 'none' : undefined} style={{ cursor: !hasSelectedRange ? 'not-allowed' : 'pointer' }} ta='right'>
                Clear Selection
              </Anchor>)}
          </Stack>
        </Flex>
      </Box>
      {bottomSection && <Box>{bottomSection}</Box>}
    </>);
};
